import { Navigate, useParams } from 'react-router-dom';
import { useWorkpaperContext } from '../providers/WorkpaperProvider';
import { useCalculation } from '../queries/use-calculation';
import SchemaContainer from '../features/worksheet-v2/custom-worksheet-v2/SchemaContainer';
import { Worksheet } from '../types/worksheet';
import NavBreadcrumbs from '../components/nav-breadcrumbs/NavBreadcrumbs';
import { getFieldCount } from '../features/worksheet-v2/custom-worksheet-v2/store/utils';
import { validate as isUUID } from 'uuid';
import { WorksheetSchema } from '../types/custom-worksheet-v2';
import ErrorSplashScreen from './ErrorSplashScreen';
import { useFirmContext } from '../providers/FirmProvider';
import CalculationInfo from '../features/worksheet-v2/calculations/CalculationInfo';
import WorksheetPreview from '../features/worksheet-preview/WorksheetPreview';

function CalculationPage(): JSX.Element {
    const { id: workpaperId } = useWorkpaperContext();
    const { id } = useParams();
    const { shortId } = useFirmContext();

    const { data: calculation, isLoading } = useCalculation(workpaperId, id || '', isUUID(id || ''));

    if (isLoading) {
        return <></>;
    }
    if (!isUUID(id ?? '')) {
        return <Navigate to={`/${shortId}/workpapers/${workpaperId}/calculations`} />;
    }
    if (!calculation) {
        return (
            <ErrorSplashScreen
                errorCode={404}
                title="Calculation not found."
                redirectTitle="Back to calculations"
                redirectUrl={`/${shortId}/workpapers/${workpaperId}/calculations`}
                leftArrow={true}
            />
        );
    }
    const progress = getCalculationProgress(calculation.schema);

    return (
        <div className="flex">
            <div className="w-full flex flex-col h-full gap-4 items-center">
                <div className="w-full max-w-[1250px] ">
                    <div className="flex flex-col justify-between flex-wrap gap-2 mb-4">
                        <div className="mb-[-8px]">
                            <NavBreadcrumbs
                                paths={[
                                    {
                                        label: 'Calculations',
                                        to: `/${shortId}/workpapers/${workpaperId}/calculations`,
                                    },
                                ]}
                            />
                        </div>
                        <CalculationInfo
                            name={calculation.name}
                            description={calculation.description}
                            progress={progress}
                            worksheets={calculation.worksheets}
                        />
                    </div>
                    <SchemaContainer calculation={calculation} />
                </div>
            </div>
            <WorksheetPreview />
        </div>
    );
}
export default CalculationPage;

function getCalculationProgress(schema: WorksheetSchema): number {
    const { total, completed } = getFieldCount(schema, true);
    return total === 0 ? 0 : Math.floor((completed / total) * 100);
}
